<template>
  <div class="quote-page">
    <div class="quote-hero">
      <div class="quote-grid">
        <div class="quote-intro">
          <h1 class="quote-title">Get a bike insurance quote</h1>
          <p class="quote-sub">See your price in about two minutes.</p>
          <p class="quote-sub-2">
            Then hold your price for 30 days while you decide.
          </p>

          <!-- Trust strip: one row on desktop, wrapping on a phone. -->
          <ul class="quote-trust">
            <li class="quote-trust-rating">
              <RatingStars :size="14" />
              <span>{{ ratingTextShort }} · 50,000+ cyclists covered</span>
            </li>
            <li>Claims assessed within 24 hours</li>
            <li>
              <nuxt-link to="/price-match-promise">
                Price Match Promise
              </nuxt-link>
            </li>
          </ul>

          <!-- Only rendered for a bike type we recognise, and only ever as the
               label from our own list — the query value itself never reaches
               the DOM. -->
          <p v-if="bikeLabel" class="quote-chip">
            <span class="quote-chip-label">{{ bikeLabel }}</span>
            <button type="button" class="quote-chip-change" @click="clearBike">
              Change
            </button>
          </p>
        </div>

        <!-- Same estimator component /calculator renders, so the two pages
             cannot show a different price for the same inputs. -->
        <QuoteEstimator
          :initial-bike-value="bikeValue"
          :initial-age="age"
          :initial-price="price"
          footnote="Estimate only — your exact quote takes about two minutes."
          secondary-link-text="See what’s covered"
          secondary-link-href="/policy-information"
          @continue="onContinue"
        />
      </div>
    </div>

    <div class="quote-body">
      <section class="quote-block">
        <h2>What you’ll need</h2>
        <ul class="quote-list">
          <li>
            Your bike’s make, model and what it would cost to replace today
          </li>
          <li>Your postcode and date of birth</li>
          <li>
            That’s it — you’ll see your price before anything else is asked of
            you
          </li>
        </ul>
      </section>

      <section class="quote-block">
        <h2>Can we cover your bike?</h2>
        <ul class="quote-list">
          <li>Bikes worth £400 to £15,000 each</li>
          <li>Up to five bikes on one policy, £30,000 combined</li>
          <li>You’re 16 or over and resident in the UK or the Isle of Man</li>
          <li>You own the bike, and it has no existing damage</li>
          <li>
            In the past year you haven’t made more than two bicycle-related
            claims, or a single bicycle-related claim over £500
          </li>
        </ul>
        <p class="quote-note">
          <strong>Insuring an e-bike?</strong> It must be road-legal in the UK
          as an electrically assisted pedal cycle (EAPC). Twist-and-go throttles
          aren’t eligible unless the throttle only works in walk mode.
        </p>
      </section>

      <section class="quote-block">
        <h2>What’s included</h2>
        <ul class="quote-list">
          <li>Theft at home and away from home</li>
          <li>Accidental and malicious damage, with Theft + Damage cover</li>
          <li>Replacement bike hire up to £500</li>
        </ul>
        <p class="quote-note">
          Accessories, clothing and bike boxes, personal accident and personal
          liability are selectable, and overseas and race cover are available
          for an additional premium.
        </p>
        <p class="quote-link">
          <nuxt-link to="/policy-information">
            Every limit and exclusion
          </nuxt-link>
        </p>
      </section>

      <section class="quote-block">
        <h2>Questions people ask at this point</h2>
        <!-- Plain markup, not an accordion: every answer has to be in the raw
             HTML, and the FAQ schema below quotes this same wording. -->
        <dl class="quote-faq">
          <template v-for="faq in faqs">
            <dt :key="`q-${faq.question}`">{{ faq.question }}</dt>
            <dd :key="`a-${faq.question}`">{{ faq.answer }}</dd>
          </template>
        </dl>
      </section>
    </div>
  </div>
</template>

<script>
import QuoteEstimator from '@/components/common/QuoteEstimator'
import RatingStars from '@/components/RatingStars'
import { RATING_TEXT_SHORT } from '@/helpers/rating'
import { getCanonicalUrl } from '@/helpers/seo'
import { jsonLdScript } from '@/helpers/blog-schema'
import { buildSocialMeta, resolveSocialImage } from '@/helpers/social-meta'
import {
  bikeTypeLabel,
  fetchInitialEstimate,
  parseEstimatorQuery,
} from '@/helpers/estimator'

const CANONICAL_PATH = '/quote'
const OG_IMAGE_PATH = '/images/og/quote-1200x630.png'

// One list, used for the visible page and for the FAQPage node, so the two can
// never drift — Google treats schema that doesn't match the page as a
// violation, and it is the kind of drift nobody notices for months.
const FAQS = [
  {
    question: 'How long does it take?',
    answer:
      'About two minutes. You’ll need your bike’s details, your postcode and your date of birth.',
  },
  {
    question: 'Do I have to pay to get a quote?',
    answer:
      'No. You can get a full, personalised quote before paying anything, and it’s held at that price for 30 days.',
  },
  {
    question: 'Can I pay monthly?',
    answer:
      'Yes, in 12 monthly payments. Your price doesn’t change during the policy year.',
  },
  {
    question: 'Can I insure more than one bike?',
    answer:
      'Yes, up to five bikes on one policy, with a combined value of up to £30,000 and a maximum of £15,000 for any single bike.',
  },
  {
    question: 'Will you need photos of my bike?',
    answer:
      'Yes. After you buy, you’ll need to upload photos of your insured bikes within 30 days. It takes two minutes in your dashboard, and it’s a policy condition, so don’t skip it.',
  },
]

export default {
  name: 'QuotePage',
  components: { QuoteEstimator, RatingStars },
  layout: 'free',
  // The price is resolved before render, so the raw HTML a crawler receives
  // carries a real monthly and annual figure rather than £0.00.
  async asyncData({ $axios, query }) {
    const { bike, bikeValue, age, src } = parseEstimatorQuery(query)
    const { entryPrice, price } = await fetchInitialEstimate($axios, {
      bikeValue,
      age,
    })

    return { bike, bikeValue, age, src, entryPrice, price }
  },
  data() {
    return {
      bike: '',
      bikeValue: null,
      age: null,
      src: '',
      entryPrice: null,
      price: null,
      faqs: FAQS,
      // Kept in one place because it is a regulated claim: it has to match
      // what the business can substantiate, and what the homepage shows.
      ratingTextShort: RATING_TEXT_SHORT,
    }
  },
  head() {
    const title = 'Get a Bike Insurance Quote | Pedalsure'
    const description =
      'Get a personalised bike insurance quote in about two minutes. See your price before you pay anything, and hold it for 30 days. Cover for bikes worth £400 to £15,000.'
    const canonicalUrl = getCanonicalUrl(CANONICAL_PATH)

    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        // The layout's canonical is already self-referencing; restated here so
        // it survives any future change to how the layout builds it.
        { hid: 'robots', name: 'robots', content: 'index,follow' },
        // path is the fixed CANONICAL_PATH, not $route.path - so a tracked
        // variant like /quote?src=nav still publishes the one canonical
        // og:url instead of fragmenting into a per-link social identity.
        ...buildSocialMeta({
          title,
          description,
          path: CANONICAL_PATH,
          image: OG_IMAGE_PATH,
          type: 'website',
        }).meta,
      ],
      link: [{ hid: 'canonical', rel: 'canonical', href: canonicalUrl }],
      script: [jsonLdScript('ld-quote', this.structuredData)],
    }
  },
  computed: {
    bikeLabel() {
      return bikeTypeLabel(this.bike)
    },
    structuredData() {
      const graph = []

      // The Offer is published only when this render got a real figure from
      // the pricing engine. Asserting a price we cannot stand behind is worse
      // than publishing no Offer at all.
      if (this.entryPrice) {
        const price = this.entryPrice.monthly.toFixed(2)
        graph.push({
          '@type': 'Product',
          '@id': `${getCanonicalUrl(CANONICAL_PATH)}#product`,
          name: 'Pedalsure bicycle insurance',
          image: [resolveSocialImage(OG_IMAGE_PATH)],
          description:
            'Bicycle insurance covering theft, accidental and malicious damage, at a fixed price for the policy year.',
          brand: { '@type': 'Brand', name: 'Pedalsure' },
          offers: {
            '@type': 'Offer',
            '@id': `${getCanonicalUrl(CANONICAL_PATH)}#offer`,
            url: getCanonicalUrl(CANONICAL_PATH),
            priceCurrency: 'GBP',
            price,
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price,
              priceCurrency: 'GBP',
              unitCode: 'MON',
              description: `From £${price} per month, inclusive of Insurance Premium Tax, for a bike valued at £${this.entryPrice.bikeValue}. Your price depends on bike value, age, postcode and cover selected, and is held for 30 days once quoted.`,
            },
            availability: 'https://schema.org/InStock',
            areaServed: ['GB', 'IM'],
          },
        })
      }

      graph.push({
        '@type': 'FAQPage',
        '@id': `${getCanonicalUrl(CANONICAL_PATH)}#faq`,
        mainEntity: FAQS.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      })

      graph.push({
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: getCanonicalUrl('/'),
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Get a quote',
            item: getCanonicalUrl(CANONICAL_PATH),
          },
        ],
      })

      return { '@context': 'https://schema.org', '@graph': graph }
    },
  },
  methods: {
    // Drops the chip and the parameter behind it, without a navigation that
    // would throw away the price already on screen.
    clearBike() {
      this.bike = ''
      if (typeof window === 'undefined' || !window.history) return
      const query = { ...this.$route.query }
      delete query.bike
      const search = Object.keys(query)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`
        )
        .join('&')
      window.history.replaceState(
        window.history.state,
        '',
        search ? `${CANONICAL_PATH}?${search}` : CANONICAL_PATH
      )
    },
    onContinue({ bikeValue, age }) {
      this.$gtm.push({
        event: 'quote_page_continue',
        bike_value: bikeValue,
        age,
        bike_type: this.bike || null,
        quote_source: this.src || null,
      })
      this.$modal.show('quick-quote', {
        prefill: {
          bikeValue,
          age,
          bike: this.bike || null,
          // Only the e-bike page answers this for the customer; every other
          // bike type leaves the question to them.
          isEbike: this.bike === 'electric' ? true : null,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Mobile first: the H1, both sliders, the price and the button all have to
// land in the first screenful at 375px, so the rhythm is carried by two
// variables that step down on short viewports.
.quote-page {
  --gap: 14px;
  --pad: 16px;

  padding: 5px 12px 40px;
  @include tablet {
    padding: 32px 24px 64px;
  }

  @media screen and (max-height: 700px) {
    --gap: 10px;
    --pad: 12px;
  }
}

.quote-hero {
  max-width: 1120px;
  margin: 0 auto;
  padding: var(--pad);
  border-radius: $ps-radius-lg;
  background: $ps-primary;
  @include tablet {
    padding: 40px;
  }
}

// Source order is the mobile order: heading, sliders, then the price with the
// one action directly under it. On desktop the price card lifts into its own
// column so the CTA sits beside the heading rather than below everything.
.quote-grid {
  display: grid;
  gap: var(--gap);
  @include desktop {
    grid-template-columns: minmax(0, 1fr) 400px;
    grid-template-areas:
      'intro   side'
      'sliders side';
    align-items: start;
    gap: 24px 32px;
  }
}
@include desktop {
  .quote-intro {
    grid-area: intro;
  }
}

.quote-title {
  font-size: 28px;
  line-height: 1.12;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: $ps-white;
  @include tablet {
    font-size: clamp(32px, 5vw, 50px);
    line-height: 1.08;
  }
  @media screen and (max-height: 700px) {
    font-size: 22px;
  }
}
.quote-sub {
  margin-top: 8px;
  font-size: $ps-text-small;
  line-height: 1.45;
  color: $ps-white;
  @include tablet {
    margin-top: 14px;
    font-size: $ps-text-body;
  }
}
// The second line is the differentiator rather than the promise, so it is the
// first thing to give up its space when the fold is tight.
.quote-sub-2 {
  display: none;
  @include tablet {
    display: block;
    margin-top: 4px;
    font-size: $ps-text-body;
    line-height: 1.5;
    // 4.9:1 on the indigo ground — comfortably past AA for body text.
    color: rgba(255, 255, 255, 0.86);
  }
}

.quote-trust {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  margin-top: 12px;
  font-size: $ps-text-label;
  color: $ps-white;

  li {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  // On a phone the sentence can't sit beside the stars; it drops, whole, onto
  // its own line under them instead of being squeezed into a narrow column.
  .quote-trust-rating {
    flex-wrap: wrap;
    row-gap: 3px;

    > span {
      white-space: nowrap;
    }
  }

  a {
    color: $ps-white;
    text-decoration: underline;
  }

  @include tablet {
    margin-top: 18px;
    font-size: $ps-text-small;

    // Items wrap between each other (never inside) when the hero column is
    // too narrow for one row - forcing nowrap squeezed every item into a
    // column once the rating gained its stars. The rating keeps stars and
    // sentence on one line here.
    .quote-trust-rating {
      flex-wrap: nowrap;
    }
  }
}

.quote-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 6px 8px 6px 14px;
  border-radius: $ps-radius-pill;
  background: rgba(255, 255, 255, 0.16);
}
.quote-chip-label {
  font-size: $ps-text-label;
  font-weight: 600;
  color: $ps-white;
}
.quote-chip-change {
  min-height: $ps-tap-min;
  padding: 0 10px;
  border: 0;
  border-radius: $ps-radius-pill;
  background: transparent;
  font-size: $ps-text-label;
  color: $ps-white;
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $ps-white;
    outline-offset: 2px;
  }
}

// --- Below the fold -----------------------------------------------------
.quote-body {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 4px;
}
.quote-block {
  margin-top: 32px;

  h2 {
    font-size: $ps-text-h3;
    font-weight: 700;
    line-height: 1.25;
    color: $ps-ink;
    @include tablet {
      font-size: $ps-text-h2;
    }
  }
}
.quote-list {
  margin-top: 12px;

  li {
    position: relative;
    padding-left: 22px;
    font-size: $ps-text-small;
    line-height: 1.55;
    color: $ps-body-color;
  }

  li + li {
    margin-top: 8px;
  }

  li::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 0.62em;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $ps-primary;
  }
}
.quote-note {
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: $ps-radius-md;
  background: $ps-surface;
  font-size: $ps-text-small;
  line-height: 1.55;
  color: $ps-body-color;

  strong {
    color: $ps-ink;
  }
}
.quote-link {
  margin-top: 12px;
  font-size: $ps-text-small;

  a {
    color: $ps-primary;
    text-decoration: underline;
  }
}

.quote-faq {
  margin-top: 12px;

  dt {
    margin-top: 18px;
    font-size: $ps-text-body;
    font-weight: 700;
    color: $ps-ink;
  }

  dd {
    margin-top: 6px;
    font-size: $ps-text-small;
    line-height: 1.55;
    color: $ps-body-color;
  }
}
</style>
